import { MAIN_INCOME_SOURCE } from '@Types/consts.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { getCustomerModel } from './customer.service.ts';
import { CustomerDataSchema, OnboardingTypeEnum, UBORoles } from './customer.types';
import type { StateCreator } from 'zustand';
import type { CustomerData, ICustomerSlice } from './customer.types';
import type { IOnboardingSlice } from '@Types/onboarding.types.ts';

const filterPrimaryOwnerFromUbos = (data: CustomerData): CustomerData => {
  const uboList = data.customerData.customerBusinessData.businessUbos?.uboResponseList;
  if (!uboList?.length) return data;

  return {
    ...data,
    customerData: {
      ...data.customerData,
      customerBusinessData: {
        ...data.customerData.customerBusinessData,
        businessUbos: {
          uboResponseList: uboList.filter((ubo) => ubo.role !== UBORoles.PRIMARY_OWNER),
        },
      },
    },
  };
};

export const createCustomerSlice: StateCreator<
  ICustomerSlice & IOnboardingSlice,
  [],
  [],
  ICustomerSlice
> = (set, get) => ({
  customer: CustomerDataSchema.parse(getCustomerModel()) as CustomerData,
  isBusinessBuildEnabled: false,
  setIsBusinessBuildEnabled: (isBusinessBuildEnabled) => {
    set(() => ({ isBusinessBuildEnabled }));
  },
  setCustomerData: (data) => {
    set(() => ({ customer: filterPrimaryOwnerFromUbos(data) }));
  },
  setCustomerAccessToken: (token) =>
    set(() => ({ customer: { ...get().customer, accessToken: token } })),
  getCustomerData: () => get().customer,
  isMultiBusiness: () =>
    get().customer.customerData.onboardingTypeEnum === OnboardingTypeEnum.MULTI_BUSINESS ||
    get().onboardingSlice.onboardingFlowIdentifier ===
      OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS,
  isAccountantAsOwner: () =>
    get().onboardingSlice.onboardingFlowIdentifier === OnboardingFlowIdentifier.ACCOUNTANT_AS_OWNER,
  isPhoneCountryCodeUs: () => get().customer.customerData.phoneCountryCode === 'US',
  getIsUboValidForNonUsFlow: () => {
    const uboList =
      get().customer.customerData.customerBusinessData.businessUbos?.uboResponseList || [];
    return uboList.some(
      (ubo) =>
        ubo.customerSSNData?.idCountry &&
        ubo.customerSSNData.idCountry !== 'US' &&
        ubo.ownershipPercent >= 25
    );
  },
  getIsUsFlow: () => {
    return (
      get().customer.customerData.idCountry === 'US' || get().customer.customerData.idCountry === ''
    );
  },
  getIsSoleProp: () =>
    get().customer.customerData.customerBusinessData.mainIncomeSource ===
      MAIN_INCOME_SOURCE.SOLE_PROP_DBA ||
    get().customer.customerData.customerBusinessData.mainIncomeSource ===
      MAIN_INCOME_SOURCE.SOLE_PROP_NO_DBA,
  setAccessToken: (token) => set(() => ({ customer: { ...get().customer, accessToken: token } })),
});
